// src/utils/browserHelper.js
import { Browser } from '@capacitor/browser';
import { isCapacitor, isElectron } from './platform';

/**
 * Opens a URL in the appropriate browser based on the platform
 * - On Capacitor (mobile): Opens in-app browser with custom styling
 * - On Electron: Opens in system default browser
 * - On Web: Opens in a new tab
 *
 * @param {string} url - The URL to open
 * @returns {Promise<void>}
 */
export async function openExternalUrl(url) {
  if (!url) {
    throw new Error('URL is required');
  }

  if (isCapacitor) {
    // Open in Capacitor in-app browser with custom settings
    await Browser.open({
      url,
      presentationStyle: 'popover', // iOS: modal presentation, Android: no effect
      toolbarColor: '#960018', // Match app branding (voxia red)
      windowName: '_blank',
    });
  } else if (isElectron && window.electronAPI?.openExternal) {
    // Electron: open in system default browser
    window.electronAPI.openExternal(url);
  } else {
    // Web: open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Closes the in-app browser (only works on Capacitor)
 * Safe to call on all platforms - will be ignored on web/electron
 *
 * @returns {Promise<void>}
 */
export async function closeInAppBrowser() {
  if (isCapacitor) {
    await Browser.close();
  }
}

/**
 * Adds a listener for when the in-app browser is closed
 * Only works on Capacitor - safe to call on all platforms
 *
 * @param {Function} callback - Function to call when browser closes
 * @returns {Function} Cleanup function to remove the listener
 */
export function onBrowserClosed(callback) {
  if (!isCapacitor) {
    // Return no-op cleanup function for web/electron
    return () => {};
  }

  // Add listener for browser close event
  const listener = Browser.addListener('browserFinished', () => {
    callback();
  });

  // Return cleanup function
  return () => {
    listener.remove();
  };
}
